/** Graphics tiers. Auto picks by device class; software GL (CI, VMs) always gets the light tier. */
export type QualityLevel = 'ultra' | 'high' | 'medium' | 'low';
export type QualityChoice = QualityLevel | 'auto';

export interface Quality {
  level: QualityLevel;
  /** Upper bound for the device pixel ratio; dynamic resolution scales below it. */
  maxPixelRatio: number;
  minPixelRatio: number;
  shadowMap: number;
  shadowRange: number;
  post: boolean;
  msaa: number;
  bloom: boolean;
  /** Terrain grid spacing (m) in the arena core, and track-band row spacing (m). */
  terrainStep: number;
  bandStep: number;
  grass: number;
  trees: number;
  skyFace: number;
  frameRate: number;
  /** Software rasteriser (CI, VMs): start at the lowest resolution. */
  software: boolean;
}

const TIERS: Record<QualityLevel, Omit<Quality, 'level' | 'software'>> = {
  ultra: { maxPixelRatio: 2, minPixelRatio: 1, shadowMap: 4096, shadowRange: 46, post: true, msaa: 4, bloom: true, terrainStep: 3, bandStep: .8, grass: 1, trees: 1, skyFace: 1024, frameRate: 120 },
  high: { maxPixelRatio: 1.75, minPixelRatio: .85, shadowMap: 4096, shadowRange: 42, post: true, msaa: 4, bloom: true, terrainStep: 4, bandStep: 1, grass: .75, trees: .9, skyFace: 768, frameRate: 60 },
  medium: { maxPixelRatio: 1.35, minPixelRatio: .75, shadowMap: 2048, shadowRange: 36, post: true, msaa: 0, bloom: true, terrainStep: 6, bandStep: 1.6, grass: .4, trees: .65, skyFace: 512, frameRate: 60 },
  low: { maxPixelRatio: 1, minPixelRatio: .6, shadowMap: 1024, shadowRange: 32, post: false, msaa: 0, bloom: false, terrainStep: 12, bandStep: 3.5, grass: 0, trees: .4, skyFace: 256, frameRate: 40 },
};

const KEY = 'tq-graphics';
export function storedQuality(): QualityChoice {
  const url = new URLSearchParams(location.search).get('gfx');
  if (url && (url === 'auto' || url in TIERS)) return url as QualityChoice;
  try { const v = localStorage.getItem(KEY); if (v && (v === 'auto' || v in TIERS)) return v as QualityChoice; } catch { /* storage blocked */ }
  return 'auto';
}
export function saveQuality(choice: QualityChoice) { try { localStorage.setItem(KEY, choice); } catch { /* storage blocked */ } }

/** Renderer string, e.g. "ANGLE (Apple, ANGLE Metal Renderer: Apple M4, …)". */
export function gpuName(gl: WebGLRenderingContext | WebGL2RenderingContext) {
  const info = gl.getExtension('WEBGL_debug_renderer_info');
  return String(info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
}

export function resolveQuality(choice: QualityChoice, gl: WebGLRenderingContext | WebGL2RenderingContext): Quality {
  const gpu = gpuName(gl);
  const software = /swiftshader|llvmpipe|software|softpipe/i.test(gpu);
  let level: QualityLevel;
  if (choice !== 'auto') level = choice;
  else if (software) level = 'low';
  else if (matchMedia('(pointer: coarse)').matches) level = 'medium';
  else level = 'high';
  // Software rasterisers cannot afford post-processing at any tier.
  if (software && level !== 'low') level = 'low';
  return { level, software, ...TIERS[level] };
}

export const QUALITY_LABELS: Record<QualityChoice, string> = {
  auto: 'Automatic', ultra: 'Ultra', high: 'High', medium: 'Balanced', low: 'Battery saver',
};
